import { 
  Clock, 
  Users, 
  Shield, 
  Calendar, 
  Brain, 
  Smartphone, 
  TrendingUp, 
  Globe, 
  Zap, 
  FileCheck, 
  BarChart3, 
  Lock,
  Fingerprint,
  MapPin,
  Bell,
  DollarSign,
  ClipboardCheck,
  LineChart,
  Briefcase,
  Activity,
  Target,
  CheckCircle,
  AlertTriangle,
  Timer
} from 'lucide-react';
import { ProductFeatureConfig } from './types';

export const timeAttendanceSystemConfig: ProductFeatureConfig = {
  keyBenefits: [
    {
      icon: Clock,
      title: 'Real-Time Time Tracking',
      description: 'Capture every clock-in and clock-out with multi-device support including mobile, web, kiosk, and biometric terminals.',
      metrics: '99.9% accuracy',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      icon: Fingerprint,
      title: 'Biometric Authentication',
      description: 'Eliminate buddy punching with facial recognition, fingerprint scanning, and AI-powered identity verification.',
      metrics: '100% fraud prevention',
      color: 'from-purple-500 to-pink-600'
    },
    {
      icon: Brain,
      title: 'AI Smart Scheduling',
      description: 'Automate roster creation with AI that considers skills, availability, preferences, and labour demand forecasting.',
      metrics: '60% time saved',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      icon: MapPin,
      title: 'GPS Geofencing',
      description: 'Verify employee locations at clock-in with configurable geofence boundaries for mobile and remote workers.',
      metrics: 'Multi-site support',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: Shield,
      title: 'UK Compliance Built-In',
      description: 'Automatic Working Time Regulations, minimum wage, break enforcement, and junior worker hour restrictions.',
      metrics: 'Audit-ready',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Calendar,
      title: 'Leave & Absence Management',
      description: 'Comprehensive holiday, sick leave, TOIL, and absence tracking with Bradford Factor monitoring.',
      metrics: 'Full visibility',
      color: 'from-violet-500 to-purple-600'
    },
    {
      icon: DollarSign,
      title: 'Payroll Integration',
      description: 'Seamless export to all major payroll systems with automated timesheet approval and multi-rate calculations.',
      metrics: 'Zero manual entry',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Smartphone,
      title: 'Mobile-First Experience',
      description: 'Native iOS and Android apps for employees to clock, request leave, swap shifts, and view schedules anywhere.',
      metrics: '24/7 access',
      color: 'from-pink-500 to-rose-600'
    },
    {
      icon: Bell,
      title: 'Smart Alerts & Notifications',
      description: 'Proactive notifications for late arrivals, overtime thresholds, compliance warnings, and shift reminders.',
      metrics: 'Real-time alerts',
      color: 'from-amber-500 to-orange-600'
    },
    {
      icon: Users,
      title: 'Self-Service Portal',
      description: 'Empower employees to manage availability, swap shifts, request time off, and view timesheets independently.',
      metrics: '80% fewer requests',
      color: 'from-teal-500 to-cyan-600'
    },
    {
      icon: BarChart3,
      title: 'Labour Cost Analytics',
      description: 'Real-time visibility into labour costs by department, project, and cost centre with budget tracking.',
      metrics: 'Cost control',
      color: 'from-indigo-500 to-blue-600'
    },
    {
      icon: Zap,
      title: 'AI Carly Smart Search',
      description: 'Natural language queries across all time data - ask any question and get instant, actionable answers.',
      metrics: 'Instant insights',
      color: 'from-yellow-500 to-amber-600'
    }
  ],
  advancedCapabilities: [
    {
      icon: Timer,
      title: 'Overtime Rules Engine',
      description: 'Configure complex overtime rules including daily, weekly, and consecutive day thresholds with automatic calculations.',
      metrics: 'Fully customisable',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      icon: Activity,
      title: 'Bradford Factor Tracking',
      description: 'Automatic Bradford Factor calculation with configurable thresholds and management alert triggers.',
      metrics: 'Absence insights',
      color: 'from-red-500 to-rose-600'
    },
    {
      icon: Target,
      title: 'Skills-Based Scheduling',
      description: 'Match employees to shifts based on certifications, skills matrices, and competency requirements.',
      metrics: 'Optimal coverage',
      color: 'from-purple-500 to-violet-600'
    },
    {
      icon: Globe,
      title: 'Multi-Site Management',
      description: 'Centralised control across unlimited locations with site-specific rules, schedules, and reporting.',
      metrics: 'Enterprise scale',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: ClipboardCheck,
      title: 'Approval Workflows',
      description: 'Configurable multi-tier approval chains for timesheets, leave requests, overtime, and shift swaps.',
      metrics: 'Full control',
      color: 'from-green-500 to-teal-600'
    },
    {
      icon: FileCheck,
      title: 'Compliance Reporting',
      description: 'Pre-built reports for WTR, minimum wage audits, right-to-work checks, and regulatory inspections.',
      metrics: 'Always audit-ready',
      color: 'from-orange-500 to-amber-600'
    },
    {
      icon: LineChart,
      title: 'Predictive Analytics',
      description: 'AI-powered forecasting for absence patterns, overtime trends, and labour demand planning.',
      metrics: 'Future insights',
      color: 'from-pink-500 to-purple-600'
    },
    {
      icon: Briefcase,
      title: 'Agency & Contractor Management',
      description: 'Track temporary workers, agency staff, and contractors with separate rate cards and reporting.',
      metrics: 'Full workforce view',
      color: 'from-teal-500 to-emerald-600'
    },
    {
      icon: CheckCircle,
      title: 'Automated Break Enforcement',
      description: 'Ensure legal break compliance with automatic deductions and configurable break reminder alerts.',
      metrics: '100% compliance',
      color: 'from-cyan-500 to-sky-600'
    },
    {
      icon: AlertTriangle,
      title: 'Fatigue Management',
      description: 'Monitor consecutive shifts, rest periods, and maximum hours to prevent burnout and safety risks.',
      metrics: 'Employee welfare',
      color: 'from-amber-500 to-yellow-600'
    },
    {
      icon: TrendingUp,
      title: 'Productivity Metrics',
      description: 'Track and benchmark productivity, efficiency, and utilisation across teams and individuals.',
      metrics: 'Performance data',
      color: 'from-violet-500 to-indigo-600'
    },
    {
      icon: Lock,
      title: 'Data Sovereignty',
      description: 'UK-hosted data with full GDPR compliance, encryption at rest and in transit, and strict access controls.',
      metrics: 'UK data residency',
      color: 'from-slate-500 to-gray-600'
    }
  ],
  securityPerformance: [
    {
      icon: Target,
      title: 'SOC 2 & ISO 27001 Compliance',
      description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and UK data protection regulations.',
      metrics: 'Complete regulatory compliance',
      color: 'from-indigo-500 to-purple-600'
    },
    {
      icon: Lock,
      title: 'End-to-End Encryption',
      description: 'All time data encrypted at rest and in transit with AES-256 encryption and secure key management.',
      metrics: 'Military-grade security',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Zap,
      title: 'Scalable Cloud Infrastructure',
      description: 'Enterprise-grade cloud platform with auto-scaling, global CDN, and 99.99% uptime SLA for reliable workforce management.',
      metrics: '99.99% uptime guaranteed',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Shield,
      title: 'DDoS Protection & WAF',
      description: 'Advanced DDoS mitigation with web application firewall for protection against cyber threats and attacks.',
      metrics: '100% attack mitigation',
      color: 'from-red-500 to-pink-600'
    },
    {
      icon: Fingerprint,
      title: 'Multi-Factor Authentication',
      description: 'Enterprise SSO integration with biometric authentication, hardware tokens, and risk-based access controls.',
      metrics: 'Zero-trust access control',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      icon: Activity,
      title: 'Comprehensive Audit Trails',
      description: 'Complete logging of all clock events, timesheet changes, approvals, and system access for compliance and security.',
      metrics: 'Complete audit visibility',
      color: 'from-yellow-500 to-amber-600'
    },
    {
      icon: Globe,
      title: 'UK Data Residency',
      description: 'All data hosted in UK data centres with GDPR compliance, data sovereignty guarantees, and strict access controls.',
      metrics: 'Zero data loss guarantee',
      color: 'from-emerald-500 to-green-600'
    },
    {
      icon: Bell,
      title: 'Real-Time Security Monitoring',
      description: 'Continuous network traffic analysis, intrusion detection, anomaly alerts, and automated threat response systems.',
      metrics: 'Real-time threat detection',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: CheckCircle,
      title: 'Continuous Security Assessment',
      description: 'Regular penetration testing, vulnerability scanning, and security audits with automated remediation procedures.',
      metrics: 'Proactive security validation',
      color: 'from-cyan-500 to-blue-600'
    }
  ]
};
